/**
 * 通用数据表格组件 — 基于 MUI Table。
 * 支持列配置、分页、空状态、行点击。
 */
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
} from "@mui/material";
import type { ReactNode } from "react";
import EmptyState from "./EmptyState";

/** 列定义。 */
export interface DataTableColumn<T> {
  /** 列唯一标识。 */
  key: string;
  /** 列标题（支持节点，如复选框全选）。 */
  label: ReactNode;
  /** 单元格渲染函数。 */
  render: (row: T) => ReactNode;
  /** 列宽度。 */
  width?: number | string;
  /** 对齐方式。 */
  align?: "left" | "right" | "center";
}

export interface DataTableProps<T> {
  /** 列配置数组。 */
  columns: DataTableColumn<T>[];
  /** 数据数组。 */
  data: T[];
  /** 总条数（用于分页）。 */
  total?: number;
  /** 当前页码（从 0 开始）。 */
  page?: number;
  /** 每页条数。 */
  rowsPerPage?: number;
  /** 页码变更回调。 */
  onPageChange?: (page: number) => void;
  /** 每页条数变更回调。 */
  onRowsPerPageChange?: (size: number) => void;
  /** 行点击回调。 */
  onRowClick?: (row: T) => void;
  /** 是否显示分页。 */
  showPagination?: boolean;
  /** 空状态提示文字。 */
  emptyText?: string;
  /** 行唯一键生成函数。 */
  getRowId?: (row: T, index: number) => string;
}

export default function DataTable<T>({
  columns,
  data,
  total = 0,
  page = 0,
  rowsPerPage = 20,
  onPageChange,
  onRowsPerPageChange,
  onRowClick,
  showPagination = true,
  emptyText = "暂无数据",
  getRowId,
}: DataTableProps<T>) {
  return (
    <Paper variant="outlined" sx={{ overflow: "hidden" }}>
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: "max-content" }}>
          <TableHead>
            <TableRow sx={{ bgcolor: "background.default" }}>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  align={col.align || "left"}
                  sx={{ width: col.width, whiteSpace: "nowrap" }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 6 }}>
                  <EmptyState text={emptyText} />
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, index) => (
                <TableRow
                  key={getRowId ? getRowId(row, index) : index}
                  hover={!!onRowClick}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  sx={{
                    cursor: onRowClick ? "pointer" : "default",
                    "&:hover": { bgcolor: onRowClick ? "brand.50" : undefined },
                  }}
                >
                  {columns.map((col) => (
                    <TableCell key={col.key} align={col.align || "left"}>
                      {col.render(row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {showPagination && total > 0 && (
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => onPageChange?.(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) =>
            onRowsPerPageChange?.(parseInt(e.target.value, 10))
          }
          rowsPerPageOptions={[10, 20, 50]}
          labelRowsPerPage="每页"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} 共 ${count}`
          }
          sx={{ borderTop: "1px solid", borderColor: "divider" }}
        />
      )}
    </Paper>
  );
}
